import {type Slide} from '#/types/capsule'
import {type FlashBookPage} from '#/types/flashbook'

/**
 * Which way a book is written.
 *
 * A FlashBook is creator content, not interface copy, so the locale is the
 * wrong thing to ask: a French teacher's book and an Arabic teacher's book are
 * both read by the same learner on the same device, and one app-wide `dir` can
 * only be right for one of them. The text itself is the only honest source, so
 * direction is derived from the writing — per block, then per page, then per
 * book — and a mixed page keeps each block reading the way it was written.
 *
 * The rule is the first-strong-character rule the Unicode bidi algorithm uses
 * (`P2`/`P3`), with one addition: the strong characters on each side are counted
 * and the majority wins, with the first strong character as the tie-break. That
 * is what makes a paragraph of Arabic with an English term in it read as Arabic,
 * while an English worksheet with one Arabic word in it still reads as English.
 *
 * Script, not language, is what matters here: bidi needs to know the *direction
 * of the strong characters*, and every language written in the Arabic or Hebrew
 * script is read right-to-left whether it is Arabic, Persian, Urdu or Hebrew.
 * Nothing below looks at vocabulary, so nothing below can guess wrong about what
 * a text *means* — only about which end of the line it starts at.
 */

/** The two layouts a book can be written in. */
export type FlashBookDirection = 'ltr' | 'rtl'

/**
 * Code point ranges of scripts written right-to-left.
 *
 * Deliberately ranges rather than `\p{Script=Arabic}`: Unicode property escapes
 * are a runtime feature (and one Hermes has only recently grown), while a range
 * table is the same arithmetic on every engine and is trivial to test.
 */
const RTL_SCRIPT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0590, 0x05ff], // Hebrew
  [0x0600, 0x06ff], // Arabic
  [0x0700, 0x074f], // Syriac
  [0x0750, 0x077f], // Arabic Supplement
  [0x0780, 0x07bf], // Thaana
  [0x07c0, 0x07ff], // NKo
  [0x0800, 0x083f], // Samaritan
  [0x0840, 0x085f], // Mandaic
  [0x0860, 0x086f], // Syriac Supplement
  [0x08a0, 0x08ff], // Arabic Extended-A
  [0xfb1d, 0xfb4f], // Hebrew presentation forms
  [0xfb50, 0xfdff], // Arabic presentation forms A
  [0xfe70, 0xfeff], // Arabic presentation forms B
  [0x10800, 0x10fff], // Cypriot, Phoenician, Kharoshthi…
  [0x1e800, 0x1efff], // Mende Kikakui, Adlam, Arabic maths…
]

/**
 * Code point ranges of the scripts FlashBook has to weigh the other way.
 *
 * Only letters count as strong: digits, punctuation, emoji and spaces are
 * *neutral* and are skipped, so "١٢٣" and "123" never decide anything and a
 * heading of pure punctuation cannot flip a page.
 */
const LTR_SCRIPT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0041, 0x005a], // Latin capitals
  [0x0061, 0x007a], // Latin lower case
  [0x00c0, 0x024f], // Latin-1 Supplement … Latin Extended-B
  [0x0250, 0x02af], // IPA
  [0x0370, 0x03ff], // Greek
  [0x0400, 0x052f], // Cyrillic
  [0x0530, 0x058f], // Armenian
  [0x0900, 0x0dff], // Indic scripts
  [0x0e00, 0x0e7f], // Thai
  [0x0e80, 0x0eff], // Lao
  [0x0f00, 0x0fff], // Tibetan
  [0x1000, 0x109f], // Myanmar
  [0x10a0, 0x10ff], // Georgian
  [0x1100, 0x11ff], // Hangul Jamo
  [0x1e00, 0x1eff], // Latin Extended Additional
  [0x3040, 0x30ff], // Hiragana, Katakana
  [0x3400, 0x4dbf], // CJK Unified Ideographs Extension A
  [0x4e00, 0x9fff], // CJK Unified Ideographs
  [0xa000, 0xa4cf], // Yi
  [0xac00, 0xd7af], // Hangul syllables
  [0xf900, 0xfaff], // CJK compatibility ideographs
]

function inRanges(
  codePoint: number,
  ranges: ReadonlyArray<readonly [number, number]>,
): boolean {
  for (const [from, to] of ranges) {
    if (codePoint < from) return false
    if (codePoint <= to) return true
  }
  return false
}

/** The direction this one character *forces*, or `null` if it is neutral. */
function strongDirectionOf(codePoint: number): FlashBookDirection | null {
  if (inRanges(codePoint, RTL_SCRIPT_RANGES)) return 'rtl'
  if (inRanges(codePoint, LTR_SCRIPT_RANGES)) return 'ltr'
  return null
}

/**
 * Which way a piece of writing runs, or `null` when it has no strong character
 * to judge by — a heading of digits, a single emoji, an empty block.
 *
 * `null` is a real answer, not a failure: it is how a caller knows to fall back
 * to the page it is printed on rather than to invent a direction, which is what
 * keeps an Arabic page from flipping to English because one block is a number.
 */
export function textDirection(text: string): FlashBookDirection | null {
  let rtl = 0
  let ltr = 0
  let first: FlashBookDirection | null = null

  for (const character of text) {
    const codePoint = character.codePointAt(0)
    if (codePoint === undefined) continue
    const direction = strongDirectionOf(codePoint)
    if (!direction) continue
    if (direction === 'rtl') rtl += 1
    else ltr += 1
    first ??= direction
  }

  if (rtl > ltr) return 'rtl'
  if (ltr > rtl) return 'ltr'
  return first
}

/**
 * Every string a block actually prints, in the order a reader meets them.
 *
 * A question's own text weighs more than its explanation, but *counting* the
 * words is not weighting them: the block asks "what script is this block
 * written in", and a block whose question is English and whose explanation is
 * Arabic is a mixed block either way. Listing the fields here — rather than
 * passing the whole slide — keeps the decision to the writing a reader sees,
 * and leaves ids, urls and answer keys out of it (an answer key of digits must
 * never be what sets the direction of the page).
 */
export function slideText(slide: Slide): string {
  const parts: (string | undefined)[] = [
    slide.contentTitle,
    slide.contentText,
    slide.contentAuthor,
    slide.question,
    slide.answer,
    slide.markWordsText,
    slide.dragWordsTemplate,
    slide.fillBlanksParagraph,
    slide.latex,
  ]

  slide.options?.forEach(option => parts.push(option.label))
  slide.pairs?.forEach(pair => parts.push(pair.left, pair.right))
  slide.blanks?.forEach(blank =>
    parts.push(blank.before, blank.answer, blank.after),
  )
  slide.orderItems?.forEach(item => parts.push(item.label))
  slide.accordionItems?.forEach(item => parts.push(item.title, item.body))
  slide.dragWordsBank?.forEach(word => parts.push(word))
  slide.findWordsGrid?.forEach(row => parts.push(row.join('')))
  slide.findWordsTargets?.forEach(word => parts.push(word))
  slide.complexBlanks?.forEach(blank =>
    parts.push(blank.sentence, blank.answer),
  )
  slide.fillBlanksAnswers?.forEach(entry => parts.push(entry.answer))
  slide.tableRows?.forEach(row => parts.push(row.join(' ')))
  slide.chartData?.forEach(point => parts.push(point.label))

  return parts.filter(Boolean).join('\n')
}

/** Which way one block is written, or `null` if it has no writing in it. */
export function slideDirection(slide: Slide): FlashBookDirection | null {
  return textDirection(slideText(slide))
}

/**
 * Which way a page is written: the direction of its writing as a whole, so a
 * page of Arabic with an English caption is still an Arabic page.
 *
 * `null` when nothing on the page has a strong character — an image-only page
 * belongs to the book's direction, not to its own.
 */
export function pageDirection(
  blocks: readonly Slide[],
): FlashBookDirection | null {
  return textDirection(blocks.map(slideText).join('\n'))
}

/**
 * Which way a book is written, used when a page cannot say for itself.
 *
 * This is a *fallback*, not a decision: every page with writing on it is laid
 * out by its own writing, so the book's direction only ever reaches a page of
 * figures and the furniture around it. Counting pages rather than characters is
 * deliberate — one long English page must not outweigh ten short Arabic ones.
 *
 * A book that is written in both directions equally is the book it *opens* as,
 * which is the same first-writer-wins rule the block level uses a scale down.
 */
export function bookDirection(
  pages: readonly FlashBookPage[],
): FlashBookDirection {
  let rtl = 0
  let ltr = 0
  let first: FlashBookDirection | null = null

  for (const page of pages) {
    const direction = pageDirection(page.blocks)
    if (!direction) continue
    first ??= direction
    if (direction === 'rtl') rtl += 1
    else ltr += 1
  }

  if (rtl > ltr) return 'rtl'
  if (ltr > rtl) return 'ltr'
  return first ?? 'ltr'
}

/**
 * The layout props that make one subtree run in a direction.
 *
 * One object rather than three call sites, because the three have to agree:
 * `direction` mirrors rows and the flow of inline content (web CSS, and Yoga on
 * native), `writingDirection` is what iOS reads for a text run, and `textAlign`
 * is what Android reads. Setting only one of them is a layout that is correct
 * on exactly one platform.
 */
export function directionStyle(direction: FlashBookDirection): {
  direction: FlashBookDirection
} {
  return {direction}
}

/** The text-level half of `directionStyle`: how a line itself is set. */
export function directionTextStyle(direction: FlashBookDirection): {
  textAlign: 'left' | 'right'
  writingDirection: FlashBookDirection
} {
  return {
    textAlign: direction === 'rtl' ? 'right' : 'left',
    writingDirection: direction,
  }
}
